import type { TrainingExerciseDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api';
import { exerciseMeta } from '../../lib/training';
import { Badge, Button, Dialog, ErrorBlock, LoadingBlock, useToast } from '../ui';
import { CategoryBadge, ExerciseText, ExternalLink, SketchWithLegend, SourceBadges } from './common';
import { ExerciseRemarks } from './ExerciseRemarks';
import { ExerciseVideoList, ExerciseVideoManager } from './ExerciseVideos';

/**
 * Detailansicht einer Uebung: grosse Skizze, Beschreibung und die Aktionen
 * "Kopieren und anpassen", bei eigenen Uebungen "Bearbeiten" und "Archivieren".
 */
export function ExerciseDetailDialog({
  id,
  canEdit,
  onClose,
  onEdit,
  onCopied,
}: {
  id: string;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (exercise: TrainingExerciseDto) => void;
  /** Die Kopie ist angelegt; die Seite wechselt zu ihr und oeffnet das Formular. */
  onCopied: (copy: TrainingExerciseDto) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: exercise, isLoading, error } = useQuery({
    queryKey: ['training-exercise', id],
    queryFn: () => api.get<TrainingExerciseDto>(`/api/training/exercises/${id}`),
  });

  const fail = (fallback: string) => (err: unknown) => toast.show(err instanceof ApiError ? err.message : fallback, 'error');

  const copy = useMutation({
    mutationFn: () => api.post<TrainingExerciseDto>(`/api/training/exercises/${id}/copy`, {}),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['training-exercises'] });
      toast.show(exercise && exercise.videos.length > 0 ? 'Kopie angelegt – ohne die Videos, du kannst sie jetzt anpassen' : 'Kopie angelegt – du kannst sie jetzt anpassen');
      onCopied(created);
    },
    onError: fail('Die Übung konnte nicht kopiert werden'),
  });

  const archive = useMutation({
    mutationFn: () => api.del(`/api/training/exercises/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['training-exercises'] });
      await queryClient.invalidateQueries({ queryKey: ['training-exercise'] });
      toast.show('Übung archiviert');
      onClose();
    },
    onError: fail('Die Übung konnte nicht archiviert werden'),
  });

  const meta = exercise ? exerciseMeta(exercise) : [];

  return (
    <Dialog
      open
      onClose={onClose}
      title={exercise?.title ?? 'Übung'}
      wide
      footer={<Button variant="secondary" onClick={onClose}>Schließen</Button>}
    >
      {isLoading && <LoadingBlock />}
      {error && <ErrorBlock error={error} />}
      {exercise && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-1.5">
            <SourceBadges exercise={exercise} />
            <CategoryBadge category={exercise.category} />
            {exercise.archived && <Badge tone="danger">Archiviert</Badge>}
            {exercise.copiedFromCatalogKey && <Badge tone="neutral">Kopie einer Standardübung</Badge>}
          </div>
          {(meta.length > 0 || exercise.focus.length > 0) && (
            <p className="text-sm text-slate-600">
              {meta.join(' · ')}
              {meta.length > 0 && exercise.focus.length > 0 && ' · '}
              {exercise.focus.join(', ')}
            </p>
          )}

          {canEdit && !exercise.archived && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" loading={copy.isPending} onClick={() => copy.mutate()}>
                Kopieren und anpassen
              </Button>
              {exercise.editable && (
                <>
                  <Button variant="secondary" size="sm" onClick={() => onEdit(exercise)}>Bearbeiten</Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={archive.isPending}
                    onClick={() => {
                      if (window.confirm(`„${exercise.title}“ archivieren? Bestehende Einheiten behalten die Übung.`)) archive.mutate();
                    }}
                  >
                    Archivieren
                  </Button>
                </>
              )}
            </div>
          )}

          {canEdit && !exercise.archived && exercise.videos.length > 0 && (
            <p className="hint" role="note">Beim Kopieren werden die Videos nicht mitkopiert – die Kopie beginnt ohne Videos.</p>
          )}

          {exercise.isLink && exercise.linkUrl && (
            <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
              Diese Übung liegt auf einer anderen Seite: <ExternalLink href={exercise.linkUrl}>{exercise.linkUrl}</ExternalLink>
            </p>
          )}

          <SketchWithLegend exercise={exercise} />
          {!exercise.isLink && <ExerciseText exercise={exercise} />}

          {exercise.editable && !exercise.isLink && !exercise.archived
            ? <ExerciseVideoManager exercise={exercise} />
            : <ExerciseVideoList videos={exercise.videos} />}

          <ExerciseRemarks exerciseId={exercise.id} title={exercise.title} />
        </div>
      )}
    </Dialog>
  );
}
